import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ordersApi } from "../services/orders";
import { usersApi } from "../services/users";
import type { HistoryItem } from "../types/orders";
import type { PayoutBatch } from "../types/payouts";
import { AuthContext } from "../context/AuthContext";
import { formatTxnAmount } from "../utils/currency";
import RequestDetailSheet, { DetailSheetOrder } from "./RequestDetailSheet";
import SetlistArt from "./SetlistArt";
import "./RequestHistoryModal.css";
import { FaArrowLeft } from "react-icons/fa";
import { t, formatLocale } from "../i18n";

// Request History — one chronological feed (founder redesign,
// modelled on the reference screenshot): played and declined requests mixed
// together newest-first, grouped under date dividers ("Wed, April 19 2023"),
// with a withdrawal line wherever a cashout happened. Every request row is
// the SAME fixed size — status differences live in the right-hand label,
// never in extra rows that change a card's height.
//
// DJ voluntary refunds: tapping a played row opens the request
// detail sheet, whose quiet "Refund this request" runs the founder-locked
// refund flow. A refunded request stays in the feed as a REFUNDED row (purple
// chip, struck amount) — same fixed row metrics, only the right-hand label
// changes.

type FeedEntry =
  | { kind: "order"; when: number; order: HistoryItem }
  | { kind: "withdrawal"; when: number; payout: PayoutBatch };

interface Props {
  onClose: () => void;
  /** A refund changed money state — parent refreshes earnings + the bubble. */
  onMoneyChanged?: () => void;
  // Handled DEMO practice cards as rows (lib/demoRequest demoHistoryItems),
  // merged into the feed by their real handledAt like everything else. They
  // never were Orders, so they can't come back from the /orders endpoints.
  // The amount is the price the card showed — display only, no money moved.
  demoItems?: HistoryItem[];
}

// Date formatting is assembled by hand (never one big toLocaleString call):
// iOS WebKit and Chrome disagree on joiners ("at", comma placement), and the
// reference design is exactly "Wed, April 19 2023".
const fmtDayHeader = (d: Date) =>
  `${d.toLocaleDateString(formatLocale(), { weekday: "short" })}, ${d.toLocaleDateString(formatLocale(), {
    month: "long",
  })} ${d.getDate()} ${d.getFullYear()}`;

// Per-currency nets, never blended — "$103.70" or "$103.70 | 2,000 Ft".
const fmtWithdrawal = (p: PayoutBatch) => {
  const parts = Object.entries(p.totalsByCurrency || {})
    .filter(([, t]) => (t?.net || 0) > 0)
    .map(([cur, t]) => formatTxnAmount(t.net || 0, cur));
  return parts.join(" | ");
};

const handledAt = (o: HistoryItem) =>
  new Date(o.updatedAt || o.createdAt).getTime();

const RequestHistoryModal: React.FC<Props> = ({ onClose, onMoneyChanged, demoItems }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [orders, setOrders] = useState<HistoryItem[]>([]);
  const [payouts, setPayouts] = useState<PayoutBatch[]>([]);
  const [loading, setLoading] = useState(true);
  // Tapped played/refunded row → detail sheet (refund flow lives inside it).
  const [detailOrder, setDetailOrder] = useState<HistoryItem | null>(null);

  const slug = user?.slug;
  useEffect(() => {
    const load = async () => {
      try {
        // Each leg falls back to empty on its own — a payouts hiccup must not
        // blank the whole history.
        // includeRefunded: refunded requests stay visible here as REFUNDED
        // rows (an older server ignores the param and just omits them).
        const [d, p, w] = await Promise.all([
          ordersApi.declined().catch(() => [] as HistoryItem[]),
          ordersApi.played({ includeRefunded: true }).catch(() => [] as HistoryItem[]),
          slug
            ? usersApi.payouts(slug).catch(() => [] as PayoutBatch[])
            : Promise.resolve([] as PayoutBatch[]),
        ]);
        // Failed-capture plays are excluded (founder decision): the money
        // never arrived, and a row here would either lie in green or need its
        // own explainer. Rare — a hold that died between request and play.
        setOrders(
          [...(d || []), ...(p || [])].filter((o) => !o.captureFailed)
        );
        setPayouts(w || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  // One feed: requests + withdrawals + the demo rows, newest first.
  const feed = useMemo<FeedEntry[]>(() => {
    const entries: FeedEntry[] = orders.map((o) => ({
      kind: "order",
      when: handledAt(o),
      order: o,
    }));

    for (const d of demoItems || []) {
      entries.push({ kind: "order", when: new Date(d.updatedAt || "").getTime() || 0, order: d });
    }

    for (const p of payouts) {
      entries.push({ kind: "withdrawal", when: new Date(p.createdAt).getTime() || 0, payout: p });
    }

    return entries.sort((a, b) => b.when - a.when);
  }, [orders, payouts, demoItems]);

  // Backdrop closes on a click that BEGAN on the backdrop. iOS Safari fires a
  // synthesized click ~300ms after touchend at the same point; when the modal
  // is opened by a tap low on the page (the Requests-page "See all"), that
  // ghost click lands on the freshly-mounted backdrop and closed the modal
  // instantly — it looked like the button did nothing. Requiring the pointer
  // to have gone DOWN on the backdrop ignores that stray click, and also
  // fixes the ordinary drag-from-inside-to-outside close.
  const downOnBackdrop = useRef(false);

  // Rendered feed: a date divider whenever the (local) day changes, then the
  // day's rows. Withdrawals sit inline at their own timestamp.
  const rendered: React.ReactNode[] = [];
  let lastDayKey = "";
  for (const e of feed) {
    const d = new Date(e.when);
    const dayKey = d.toDateString();
    if (dayKey !== lastDayKey) {
      lastDayKey = dayKey;
      rendered.push(
        <div className="rh-divider" key={`day-${dayKey}`}>
          {fmtDayHeader(d)}
        </div>
      );
    }

    if (e.kind === "withdrawal") {
      const failed = e.payout.payoutStatus === "failed";
      // Deliberately minimal (founder decision): the date comes from the
      // group header and the full story (status, bank, per-order breakdown)
      // already lives in Payout History — repeating it here is noise. The
      // line marks the moment and the amount; tapping it opens the real page.
      rendered.push(
        <button
          type="button"
          className="rh-divider rh-withdrawal"
          key={`w-${e.payout._id}`}
          onClick={() => {
            onClose();
            navigate("/payout-history");
          }}
        >
          {failed ? t("requestHistoryModal.withdrawalFailed") : t("requestHistoryModal.withdrawal")}
          <span className="rh-withdrawal-amount">{fmtWithdrawal(e.payout)}</span>
          <span className="rh-withdrawal-go" aria-hidden="true">›</span>
        </button>
      );
      continue;
    }

    const o = e.order;
    const played = o.status === "played";
    const refunded = o.status === "refunded";
    // Only a still-declined order is "awaiting the counter" — an order played
    // after its counter was paid keeps declineReason and must read as played.
    // A fan who declines a counter just never pays, so that row stays
    // "Countered" too — same state in the data as never-replied.
    const countered =
      o.status === "declined" && o.declineReason === "more_money" && (o.counterAmount ?? 0) > 0;

    // Played + refunded rows open the detail sheet (where the quiet refund
    // action lives). Declined rows and the demo stay inert — no action there.
    const tappable = (played || refunded) && !o.isDemo;

    rendered.push(
      <div
        className={`rh-row ${tappable ? "rh-row--tappable" : ""}`}
        key={o._id}
        {...(tappable
          ? {
              role: "button",
              tabIndex: 0,
              onClick: () => setDetailOrder(o),
              onKeyDown: (ev: React.KeyboardEvent) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  setDetailOrder(o);
                }
              },
            }
          : {})}
      >
        {o.isPlaylist ? (
          <span className="rh-art rh-art--playlist" aria-hidden>
            🎚️
          </span>
        ) : o.trackImages && o.trackImages.length ? (
          <SetlistArt images={o.trackImages} className="rh-art" />
        ) : (
          <img
            className="rh-art"
            src={o.trackImage || "/fallback.png"}
            alt=""
            onError={(ev) => {
              (ev.currentTarget as HTMLImageElement).src = "/fallback.png";
            }}
          />
        )}
        <div className="rh-mid">
          <div className="rh-title">
            {o.searchQuery}
            {o.isDemo && <span className="rh-demo-chip">{t("requestHistoryModal.demo")}</span>}
          </div>
          <div className="rh-artist">{(o.trackArtists || []).join(", ")}</div>
        </div>
        <div className="rh-right">
          {refunded ? (
            <div className="rh-refunded-chip">{t("requestHistoryModal.refunded")}</div>
          ) : (
            <div className={`rh-status ${countered ? "rh-status--countered" : ""}`}>
              {countered
                ? t("requestHistoryModal.countered")
                : played
                ? o.isPlaylist
                  ? t("requestHistoryModal.delivered")
                  : t("requestHistoryModal.played")
                : t("requestHistoryModal.declined")}
            </div>
          )}
          <div
            className={`rh-amount ${
              o.isDemo
                ? "rh-amount--demo"
                : refunded
                ? "rh-amount--refunded"
                : played
                ? "rh-amount--in"
                : "rh-amount--out"
            }`}
          >
            {played ? "+" : ""}
            {formatTxnAmount(Number(o.amount) || 0, o.currency)}
          </div>
        </div>
      </div>
    );
  }

  // Portal to <body> so the fixed overlay is always centered in the viewport,
  // never offset by an ancestor's padding/transform.
  return createPortal(
    <div
      className="modal-overlay"
      onPointerDown={(e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        if (!downOnBackdrop.current) return;
        downOnBackdrop.current = false;
        onClose();
      }}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Same circular back arrow as the Leaderboard page — one consistent
            "back to the list" affordance across the app. */}
        <button className="modal-back" onClick={onClose} aria-label={t("requestHistoryModal.back")} title={t("requestHistoryModal.back")}>
          <FaArrowLeft size={16} />
        </button>

        <h3 className="modal-title">{t("requestHistoryModal.requestHistory")}</h3>

        {loading ? (
          <p className="modal-loading">{t("requestHistoryModal.loading")}</p>
        ) : rendered.length === 0 ? (
          <p className="empty-state">{t("requestHistoryModal.songsYouPlayOr")}</p>
        ) : (
          rendered
        )}
      </div>

      {detailOrder && (
        <RequestDetailSheet
          order={detailOrder as DetailSheetOrder}
          onClose={() => setDetailOrder(null)}
          onRefunded={(orderId) => {
            // Flip the row in place — the server is the source of truth and
            // the next fetch confirms, but the talent sees it immediately.
            setOrders((prev) =>
              prev.map((o) =>
                o._id === orderId
                  ? { ...o, status: "refunded" as const, refundedAt: new Date().toISOString() }
                  : o
              )
            );
            onMoneyChanged?.();
          }}
        />
      )}
    </div>,
    document.body
  );
};

export default RequestHistoryModal;
